import * as Dialog from '@radix-ui/react-dialog';
import { Loader2 } from 'lucide-react';
import { useToggleUserFeature, useUserFeatures, UserFeature } from '../../queries/userFeatures';

interface UserFeaturesModalProps {
  userId: string;
  userName: string;
  onClose: () => void;
}

function featureStatusLabel(feature: UserFeature): string {
  const state = feature.enabled ? 'Enabled' : 'Disabled';
  return `${state} (${feature.is_default ? 'Default' : 'Override'})`;
}

const DATE_TIME_FORMATTER = new Intl.DateTimeFormat('en-AU', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export function UserFeaturesModal({ userId, userName, onClose }: UserFeaturesModalProps) {
  const { data: features = [], isLoading } = useUserFeatures(userId);
  const toggle = useToggleUserFeature(userId);

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-full max-w-3xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-border bg-background p-6 shadow-lg">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <Dialog.Title className="text-xl font-bold">User Features</Dialog.Title>
              <p className="text-muted-foreground">Manage features for {userName}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent"
            >
              Close
            </button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-3">
              {features.map((feature) => {
                const isUpdating = toggle.isPending && toggle.variables?.featureKey === feature.feature_key;
                return (
                  <div key={feature.feature_key} className="rounded-lg border border-border p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold">{feature.feature_name}</p>
                        <p className="text-sm text-muted-foreground">{feature.description}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                          {featureStatusLabel(feature)}
                        </span>
                        <button
                          type="button"
                          disabled={isUpdating}
                          onClick={() =>
                            toggle.mutate({ featureKey: feature.feature_key, enabled: !feature.enabled })
                          }
                          className={`rounded-md px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 ${
                            feature.enabled ? 'bg-red-600 hover:bg-red-700' : 'bg-primary hover:opacity-90'
                          }`}
                        >
                          {isUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> : feature.enabled ? 'Disable' : 'Enable'}
                        </button>
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
                      <div>
                        <p className="text-xs text-muted-foreground">Feature Key</p>
                        <p className="font-mono text-xs">{feature.feature_key}</p>
                      </div>
                      {feature.enabled_at && (
                        <div>
                          <p className="text-xs text-muted-foreground">Enabled At</p>
                          <p>{DATE_TIME_FORMATTER.format(new Date(feature.enabled_at))}</p>
                        </div>
                      )}
                      {feature.disabled_at && (
                        <div>
                          <p className="text-xs text-muted-foreground">Disabled At</p>
                          <p>{DATE_TIME_FORMATTER.format(new Date(feature.disabled_at))}</p>
                        </div>
                      )}
                      {feature.notes && (
                        <div className="col-span-2 sm:col-span-3">
                          <p className="text-xs text-muted-foreground">Notes</p>
                          <p>{feature.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {features.length === 0 && <p className="text-center text-muted-foreground">No features configured.</p>}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
